import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';
interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  href?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  type?: 'button' | 'submit';
  disabled?: boolean;
  target?: string;
  rel?: string;
}
const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-gold hover:bg-gold-dark text-white shadow-md',
  secondary: 'border-2 border-navy text-navy hover:bg-navy hover:text-white',
  outline: 'border-2 border-gold text-gold hover:bg-gold hover:text-white',
  ghost: 'text-gold hover:text-navy font-bold'
};
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-5 py-2 text-sm rounded-brand',
  md: 'px-6 py-3 text-base rounded-brand',
  lg: 'px-8 py-4 text-lg rounded-brand'
};
export function Button({
  variant = 'primary',
  size = 'md',
  children,
  onClick,
  className = '',
  href,
  icon,
  iconPosition = 'right',
  type = 'button',
  disabled = false,
  target,
  rel
}: ButtonProps) {
  const isGhost = variant === 'ghost';
  const baseClasses = `inline-flex items-center justify-center font-bold transition-colors duration-200 ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`;
  const classes = `${baseClasses} ${variantStyles[variant]} ${isGhost ? '' : sizeStyles[size]} ${className}`;
  const content = <>
      {icon && iconPosition === 'left' && <span className="mr-2" data-id="element-26">{icon}</span>}
      {children}
      {icon && iconPosition === 'right' && <span className="ml-2" data-id="element-27">{icon}</span>}
      {isGhost && !icon && <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-28" />}
    </>;
  if (href) {
    return <motion.a href={href} target={target} rel={rel} className={classes} whileHover={disabled ? undefined : {
      scale: 1.02
    }} whileTap={disabled ? undefined : {
      scale: 0.98
    }} data-id="element-29">
        {content}
      </motion.a>;
  }
  return <motion.button type={type} onClick={onClick} disabled={disabled} className={classes} whileHover={disabled ? undefined : {
    scale: 1.02
  }} whileTap={disabled ? undefined : {
    scale: 0.98
  }} data-id="element-30">
      {content}
    </motion.button>;
}